import { Button, Divider, Form, Input, InputNumber, Row, Select, Space, Typography, message } from 'antd';
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import API from 'helpers/API';
import { getAddress } from 'utils/AddressFinder';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { BottomButtons, HeadingH1 } from 'components/ui';

import styles from '../../orders/new.module.scss';

const cx = classNames.bind(styles);

const Page = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoadingForm, setIsLoadingForm] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadForm = async () => {
      setIsLoadingForm(true);
      const response = await API.get('/customer/orders/new-form');

      if (!isMounted) return;

      if (!response?.success) {
        message.error(getApiErrorMessage(response, '신규 주문 정보를 불러오지 못했습니다.'));
        setIsLoadingForm(false);
        return;
      }

      setPaymentMethods(response.data.paymentMethods ?? []);
      form.setFieldsValue({
        orderNo: response.data.orderNo,
        orderDate: response.data.orderDate,
        customerCompany: response.data.customer?.name,
        customerName: response.data.requester?.name,
        customerPhone: response.data.requester?.phone,
      });
      setIsLoadingForm(false);
    };

    loadForm();

    return () => {
      isMounted = false;
    };
  }, [form]);

  const findAddress = async () => {
    try {
      const data = await getAddress();
      form.setFieldValue('deliveryAddress', data.address);
    } catch (error) {
      message.error('주소를 불러오지 못했습니다. 다시 시도해주세요.');
    }
  };

  const doSave = async (values) => {
    const data = {
      quantity: Number(values.quantity),
      paymentMethod: values.paymentMethod,
      deliveryName: values.deliveryName,
      deliveryPhone: values.deliveryPhone,
      deliveryCompany: values.deliveryCompany,
      deliveryAddress: values.deliveryAddress,
      deliveryAddressDetail: values.deliveryAddressDetail,
      memo: values.memo,
    };

    setIsSubmitting(true);

    const response = await API.post('/customer/orders', data);

    if (response?.success) {
      message.success('저장을 완료하였습니다.');
      navigate('/customer/orders');
      return;
    }

    message.error(getApiErrorMessage(response, '저장을 실패했습니다.'));
    setIsSubmitting(false);
  };

  return (
    <div className={cx('container')}>
      <Space direction="vertical" size={16} className={cx('wrapper')}>
        <HeadingH1 title={'주문하기'} />
        <Form layout="vertical" onFinish={doSave} form={form} className={cx('form')}>
          <Typography.Text className={cx('title')} strong>
            주문정보
          </Typography.Text>
          <Row align={'top'} style={{ maxWidth: 640 }} justify={'space-between'}>
            <Space direction="vertical" style={{ width: '48%' }}>
              <Form.Item name={'orderNo'} label="주문번호">
                <Input disabled placeholder="저장 시 자동 발급" />
              </Form.Item>
              <Form.Item name={'quantity'} label="수량" rules={[{ required: true, message: '수량을 입력해주세요' }]}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name={'customerName'} label="주문자명" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
              <Form.Item
                name={'paymentMethod'}
                label="결제방법"
                rules={[{ required: true, message: '결제방법을 선택해주세요' }]}
              >
                <Select options={paymentMethods} placeholder="결제방법 선택" />
              </Form.Item>
            </Space>
            <Space direction="vertical" style={{ width: '48%' }}>
              <Form.Item name={'orderDate'} label="주문일자" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
              <Form.Item name={'customerCompany'} label="주문자 회사명" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
              <Form.Item name={'customerPhone'} label="주문자 전화번호" rules={[{ required: true }]}>
                <Input disabled />
              </Form.Item>
            </Space>
          </Row>
          <Divider />
          <Typography.Text className={cx('title')} strong>
            배송정보
          </Typography.Text>
          <Row align={'top'} style={{ maxWidth: 640 }} justify={'space-between'}>
            <Space style={{ width: '48%' }} direction="vertical">
              <Form.Item
                name={'deliveryName'}
                label="수신자명"
                rules={[{ required: true, message: '수신자명을 입력해주세요' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={'deliveryCompany'}
                label="수신자 회사명"
                rules={[{ required: true, message: '회사명을 입력해주세요' }]}
              >
                <Input />
              </Form.Item>
            </Space>
            <Space style={{ width: '48%' }} direction="vertical">
              <Form.Item
                name={'deliveryPhone'}
                label="수신자 전화번호"
                rules={[{ required: true, message: '전화번호를 입력해주세요' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name={'deliveryAddress'}
                label="주소"
                rules={[{ required: true, message: '주소를 입력해주세요' }]}
              >
                <Input.Search onClick={findAddress} onSearch={findAddress} loading={isLoadingForm || isSubmitting} />
              </Form.Item>
              <Form.Item name={'deliveryAddressDetail'} label="상세주소">
                <Input />
              </Form.Item>
            </Space>
          </Row>
          <Typography.Text className={cx('title')} strong>
            부가정보
          </Typography.Text>
          <Form.Item name={'memo'} label="메모">
            <Input.TextArea style={{ resize: 'none', maxWidth: 840, minHeight: 150 }} />
          </Form.Item>

          <BottomButtons
            left={[
              <Button key={'backPage'} onClick={() => navigate(-1)}>
                뒤로
              </Button>,
            ]}
            right={[
              <Button
                key={'saveOrder'}
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={isLoadingForm}
              >
                저장
              </Button>,
            ]}
          />
        </Form>
      </Space>
    </div>
  );
};

export default Page;
